import moment from "moment";
import {
    Button,
    Datagrid,
    EditButton,
    FunctionField,
    List,
    ShowButton,
    TextField,
    TopToolbar,
    useListContext,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import { Chip, Link } from "@mui/material";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import CommonFilterCard, { CommonFilterField } from "../Common/CommonFilterCard";
import StyledDatagridWrapper, { commonDatagridSx } from "../Common/StyledDatagridWrapper";
import { CampaignPageServiceEnum } from "../../interfaces/CampaignPage/CampaignPage";
import { CAMPAIGN_SITE_URL } from "./CampaignPageFormFields";

const renderBooleanChip = (value: boolean | null | undefined, trueLabel = "Yes", falseLabel = "No") => {
    if (value === undefined || value === null) return <span>-</span>;
    return (
        <Chip
            label={value ? trueLabel : falseLabel}
            size="small"
            sx={{
                backgroundColor: value ? "#d4edda" : "#f8d7da",
                color: value ? "#155724" : "#721c24",
                fontWeight: 500,
                height: 22,
            }}
        />
    );
};

const ListActions = () => {
    const { setFilters } = useListContext();
    const navigate = useNavigate();
    return (
        <TopToolbar>
            <Button
                variant="text"
                size={"small"}
                onClick={() => setFilters({}, [], false)}
                startIcon={<FilterListOffIcon />}
                label="Clear filters"
            />
            <Button
                variant="text"
                size={"small"}
                onClick={() => navigate("/CampaignPages/create")}
                startIcon={<ControlPointIcon />}
                label="Create"
            />
        </TopToolbar>
    );
};

const yesNoChoices = [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
];

const serviceChoices = Object.values(CampaignPageServiceEnum).map((value) => ({ value, label: value }));

const CampaignPageList = () => {
    const filterFields: CommonFilterField[] = [
        { type: "text", label: "Campaign Name", source: "campaignName" },
        { type: "text", label: "Slug", source: "slug" },
        { type: "select", label: "Service", source: "service", options: serviceChoices, minWidth: 160 },
        { type: "select", label: "Active", source: "isActive", options: yesNoChoices, minWidth: 140 },
    ];

    return (
        <List
            actions={<ListActions />}
            sort={{ field: "id", order: "DESC" }}
            sx={{
                "& .RaList-content": {
                    boxShadow: "none",
                    backgroundColor: "transparent",
                },
            }}
        >
            <>
            <CommonFilterCard fields={filterFields} />

            <br />

            <StyledDatagridWrapper title="Campaign Pages">
            <Datagrid bulkActionButtons={false} sx={commonDatagridSx}>
                <TextField source="id" />
                <TextField source="campaignName" label="Campaign" />
                <FunctionField
                    label="Page"
                    render={(record: any) => (
                        <Link
                            href={`${CAMPAIGN_SITE_URL}/${record.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            /campaign/{record.slug}
                        </Link>
                    )}
                />
                <TextField source="service" />
                <TextField source="bannerHeading" label="Banner Heading" />
                <FunctionField label="Active" render={(record: any) => renderBooleanChip(record?.isActive)} />
                <FunctionField
                    label="Search Engines"
                    render={(record: any) => renderBooleanChip(!record?.noindex, "Indexed", "Hidden")}
                />
                <FunctionField
                    label="Updated At"
                    render={(record: any) =>
                        record?.updatedAt ? moment(record.updatedAt).format("DD MMM YYYY, hh:mm A") : "-"
                    }
                />
                <ShowButton />
                <EditButton />
            </Datagrid>
            </StyledDatagridWrapper>
            </>
        </List>
    );
};

export default CampaignPageList;
